import { Sprout, Boxes, Check } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import ServerStatus from '@/components/feature/ServerStatus'
import Card from '@/components/base/Card'
import { LinkButton } from '@/components/base/Button'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

function FeatureList({ items, color }: { items: string[]; color: string }) {
  const { t } = useLanguage()
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2">
          <Check size={18} className={`mt-0.5 shrink-0 ${color}`} />
          <span className="text-slate-deep/80">{t(it)}</span>
        </li>
      ))}
    </ul>
  )
}

export default function ServersPage() {
  const { vanilla, modded } = serverConfig.servers
  const { t } = useLanguage()

  return (
    <>
      <PageHeader
        title="Our Servers"
        subtitle="Two distinct ways to play, both on Minecraft Java Edition. Pick the one that fits your style — or play both."
      />

      <div className="max-w-7xl mx-auto px-4 py-14 grid gap-8 lg:grid-cols-2">
        {/* Vanilla */}
        <Card variant="grass" className="flex flex-col">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-grass-dark text-white flex items-center justify-center shadow-block">
                <Sprout size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-slate-deep">{vanilla.name}</h2>
                <span className="text-grass-deep font-bold text-sm">{t('Plugins · no modpack')}</span>
              </div>
            </div>
            <ServerStatus status={vanilla.status} />
          </div>

          <p className="text-slate-deep/80 mb-5">{t(vanilla.description)}</p>

          <div className="bg-white rounded-xl border border-stone-light/60 p-4 mb-5 text-sm">
            <Row label={t('Edition')} value={`Java · ${vanilla.version}`} />
            <Row label={t('Address')} value={vanilla.host} mono />
            <Row label={t('Port')} value={String(vanilla.port)} mono />
            <p className="text-xs text-grass-deep font-bold mt-2">
              {t('Live now — connect with an unmodified Java Edition client.')}
            </p>
          </div>

          <h3 className="font-extrabold text-slate-deep mb-3">{t('What makes it special')}</h3>
          <FeatureList
            color="text-grass-dark"
            items={[
              'Land claims, economy/shops, and staff rollback keep the world fair',
              'Ranks, nametags, and cosmetic tags via LuckPerms + TAB',
              'No client modpack required — join with stock Java Edition',
              'Client-side performance mods (e.g. Sodium) are still allowed',
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <LinkButton to="/join" variant="primary">
              {t('How to join Survival')}
            </LinkButton>
            <div className="ml-auto flex flex-wrap gap-3">
              <LinkButton to="/join#commands" variant="outline">
                {t('Commands')}
              </LinkButton>
              <LinkButton to="/mods?server=vanilla" variant="outline">
                {t('View plugins')}
              </LinkButton>
            </div>
          </div>
        </Card>

        {/* Modded */}
        <Card variant="sky" className="flex flex-col">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-sky-deep text-white flex items-center justify-center shadow-block">
                <Boxes size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-slate-deep">{modded.name}</h2>
                <span className="text-sky-deep font-bold text-sm">{t('Modpack Experience')}</span>
              </div>
            </div>
            <ServerStatus status={modded.status} />
          </div>

          <p className="text-slate-deep/80 mb-5">{t(modded.description)}</p>

          <div className="bg-white rounded-xl border border-stone-light/60 p-4 mb-5 text-sm">
            <Row label={t('Edition')} value={`Java · ${modded.version}`} />
            <Row label={t('Address')} value={modded.host} mono />
            <Row label={t('Port')} value={String(modded.port)} mono />
            {modded.modpack && (
              <Row
                label={t('Modpack')}
                value={`${modded.modpack.name} · ${modded.modpack.launcher}`}
              />
            )}
            <p className="text-xs text-gold-dark font-bold mt-2">
              {t("Connection details are placeholders — the server isn't live yet.")}
            </p>
          </div>

          <h3 className="font-extrabold text-slate-deep mb-3">{t('What makes it special')}</h3>
          <FeatureList
            color="text-sky-deep"
            items={[
              'A curated modpack: tech, magic, world-gen and quality-of-life',
              'Automate everything with Create, AE2 and more',
              'New biomes, creatures and adventures to explore',
              'Installed in one click via the launcher (CurseForge / Modrinth)',
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <LinkButton to="/join" variant="sky">
              {t('How to join Modded')}
            </LinkButton>
            <LinkButton to="/mods?server=modded" variant="outline" className="ml-auto">
              {t('View mods')}
            </LinkButton>
          </div>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-slate-deep/60 font-semibold">{label}</span>
      <span className={`font-bold text-right ${mono ? 'font-mono' : ''}`} data-no-translate={mono || undefined}>
        {value}
      </span>
    </div>
  )
}
